import React, { useState, useEffect, useRef } from 'react';
import '../../css/PlaybackControls.css'; 

const PlaybackControls = ({ 
  playButton, 
  pauseButton,
  handlePlayButtonClick: externalHandlePlayButtonClick 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showTimeBubble, setShowTimeBubble] = useState(false);
  const [bubblePosition, setBubblePosition] = useState(0);
  const [bubbleTime, setBubbleTime] = useState('');
  // const [currentTime, setCurrentTime] = useState('');
  const animationRef = useRef(null);
  const radarControlRef = useRef(null);
  const progressBarRef = useRef(null);
  const [radarLoaded, setRadarLoaded] = useState(false);
  
  // 레이더 컨트롤 데이터 초기화
  useEffect(() => {
    const checkRadarControl = () => {
      if (window.radarControl) {
        radarControlRef.current = window.radarControl;
        
        // // 현재 이미지의 시간 표시 업데이트
        // if (radarControlRef.current.radarTimes && radarControlRef.current.radarTimes.length > 0) {
        //   setCurrentTime(radarControlRef.current.radarTimes[0]);
        // }
        setRadarLoaded(true);
        return true;
      }
      return false;
    };
    
    // 최초 확인
    if (!checkRadarControl()) {
      // 레이더 컨트롤이 로드될 때까지 기다림
      const interval = setInterval(() => {
        if (checkRadarControl()) {
          clearInterval(interval);
        }
      }, 500);
      
      return () => clearInterval(interval);
    }
  }, []);
  
  // 애니메이션 처리
  useEffect(() => {
    if (isPlaying && radarControlRef.current) {
      const totalImages = radarControlRef.current.totalImages;
      if (totalImages <= 1) return;
      
      const animationSpeed = 300; // 300ms 간격으로 이미지 변경
      
      const animate = () => {
        setCurrentImageIndex(prevIndex => {
          const nextIndex = (prevIndex + 1) % totalImages;
          
          // 레이더 이미지 업데이트
          if (radarControlRef.current && radarControlRef.current.updateRadarImage) {
            radarControlRef.current.updateRadarImage(nextIndex);
          }
          
          // // 시간 정보 업데이트
          // if (radarControlRef.current && radarControlRef.current.radarTimes && 
          //     radarControlRef.current.radarTimes.length > nextIndex) {
          //   setCurrentTime(radarControlRef.current.radarTimes[nextIndex]);
          // }
          
          // 프로그레스 바 업데이트
          setProgress((nextIndex / (totalImages - 1)) * 100);
          
          return nextIndex;
        });
        
        animationRef.current = setTimeout(animate, animationSpeed);
      };
      
      animationRef.current = setTimeout(animate, animationSpeed);
      
      // 컴포넌트 언마운트 시 타이머 정리
      return () => {
        if (animationRef.current) {
          clearTimeout(animationRef.current);
        }
      };
    }
  }, [isPlaying]);
  
  // 재생/일시정지 버튼 처리
  const handlePlayButtonClick = () => {
    setIsPlaying(prevState => !prevState);
    if (externalHandlePlayButtonClick) {
      externalHandlePlayButtonClick();
    }
  };
  
  // 프로그레스 바 클릭 처리
  const handleProgressBarClick = (e) => {
    if (!radarControlRef.current) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width);
    
    const totalImages = radarControlRef.current.totalImages;
    if (totalImages <= 1) return;
    
    const newIndex = Math.min(Math.floor(position * totalImages), totalImages - 1);
    
    // 레이더 이미지 업데이트
    if (radarControlRef.current.updateRadarImage) {
      radarControlRef.current.updateRadarImage(newIndex);
    }
    setCurrentImageIndex(newIndex);
    
    // // 시간 정보 업데이트
    // if (radarControlRef.current.radarTimes && radarControlRef.current.radarTimes.length > newIndex) {
    //   setCurrentTime(radarControlRef.current.radarTimes[newIndex]);
    // }
    
    // 프로그레스 바 업데이트
    setProgress((newIndex / (totalImages - 1)) * 100);
  };
  
  // 프로그레스 바 마우스 이동 처리
  const handleProgressBarMouseMove = (e) => {
    if (!radarControlRef.current || !radarControlRef.current.radarTimes) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width);
    
    const totalImages = radarControlRef.current.totalImages;
    if (totalImages <= 1) return;
    
    const index = Math.min(Math.floor(position * totalImages), totalImages - 1);
    const positionPercent = ((e.clientX - rect.left) / rect.width) * 100;
    
    setBubblePosition(Math.max(0, Math.min(positionPercent, 100)));
    setBubbleTime(radarControlRef.current.radarTimes[index] || '');
    setShowTimeBubble(true);
  };
  
  // 시작 시간과 종료 시간 표시
  const getStartTime = () => {
    if (radarControlRef.current && radarControlRef.current.radarTimes && radarControlRef.current.radarTimes.length > 0) {
      return radarControlRef.current.radarTimes[0];
    }
    return '--:--';
  };
  
  const getEndTime = () => {
    if (radarControlRef.current && radarControlRef.current.radarTimes && radarControlRef.current.radarTimes.length > 0) {
      return radarControlRef.current.radarTimes[radarControlRef.current.radarTimes.length - 1];
    }
    return '--:--';
  };
  
  // 시간 간격 마커 생성 (적절한 간격으로 표시)
  const getTimeMarkers = () => {
    if (!radarControlRef.current || !radarControlRef.current.radarTimes) return [];
    
    const totalImages = radarControlRef.current.totalImages;
    const markers = [];
    
    if (totalImages > 1) {
      // 전체 마커가 너무 많을 경우 적절하게 필터링 (예: 30분 간격)
      const interval = Math.max(1, Math.floor(totalImages / 8)); // 화면에 약 8개 마커만 표시
      
      for (let i = 0; i < totalImages; i += interval) {
        markers.push({
          position: (i / (totalImages - 1)) * 100,
          time: radarControlRef.current.radarTimes[i] || ''
        });
      }
      
      // 마지막 마커가 포함되어 있지 않다면 추가
      const lastIndex = totalImages - 1;
      if (markers.length > 0 && markers[markers.length - 1].position < 100) {
        markers.push({
          position: 100,
          time: radarControlRef.current.radarTimes[lastIndex] || ''
        });
      }
    }
    
    return markers;
  };

  if (!radarLoaded) {
    return <div className="playback-controls">레이더 데이터 로딩 중...</div>;
  }

  return (
    <div className="playback-controls">
      <div className="playback-buttons">
        <button 
          className="play-button"
          onClick={handlePlayButtonClick}
        >
          <img 
            src={isPlaying ? pauseButton : playButton} 
            alt="Play/Pause Button" 
            className="play-icon" 
          />
        </button>
        
        <div className="progress-container">
          {/* 현재 시간 표시
          <div className="current-time">
            {currentTime || '--:--'}
          </div> */}
          
          {/* 진행 바 */}
          <div 
            ref={progressBarRef}
            className="progress-bar"
            onMouseMove={handleProgressBarMouseMove}
            onMouseLeave={() => setShowTimeBubble(false)}
            onClick={handleProgressBarClick}
          >
            {/* 시간 간격 마커 */}
            {getTimeMarkers().map((marker, index) => (
              <div 
                key={index}
                className="time-marker"
                style={{ left: `${marker.position}%` }}
                title={marker.time}
              ></div>
            ))}
            
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
            
            {/* 현재 진행 위치 */}
            <div 
              className="progress-thumb"
              style={{ left: `${progress}%` }}
            ></div>
            
            {/* 말풍선 팝업 */}
            {showTimeBubble && (
              <div 
                className="time-bubble"
                style={{ left: `${bubblePosition}%` }}
              >
                <div className="bubble-content">
                  {bubbleTime}
                </div>
                <div className="bubble-arrow"></div>
              </div>
            )}
          </div>
          
          {/* 시간 레이블 */}
          <div className="time-labels">
            <span className="start-time">{getStartTime()}</span>
            <span className="end-time">{getEndTime()}</span>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybackControls;